import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Close as CloseIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import GuideButton from '../common/guide/GuideButton';
import { getBranchFirmInfo, updateBranchFirmInfo } from '../../services/migration/branchDataService';
export interface BranchCompanyInfo {
  firmName: string;
  firmTaxNumber: string;
  firmTaxOffice: string;
  firmAddress: string;
  firmPhone: string;
  firmEmail: string;
  firmWebsite: string;
  firmMersisNo: string;
  firmTradeRegistryNo: string;
}

interface BranchCompanyDetailsModalProps {
  open: boolean;
  onClose: (updated?: boolean) => void;
  companyId: string | null;
  branchId: string | null;
  branchName: string;
}

const initialData: BranchCompanyInfo = {
  firmName: '',
  firmTaxNumber: '',
  firmTaxOffice: '',
  firmAddress: '',
  firmPhone: '',
  firmEmail: '',
  firmWebsite: '',
  firmMersisNo: '',
  firmTradeRegistryNo: '',
};

const BranchCompanyDetailsModal: React.FC<BranchCompanyDetailsModalProps> = ({
  open,
  onClose,
  companyId,
  branchId,
  branchName,
}) => {
  const { t } = useTranslation('branches');
  const [details, setDetails] = useState<BranchCompanyInfo>(initialData);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    if (open && companyId && branchId) {
      setLoading(true);
      setError(null);
      setSuccessMessage(null);

      // SQL-once oku (branchDataService); HybridFallback'te Firebase companyInfo'ya duser.
      getBranchFirmInfo(companyId, branchId)
        .then((info) => {
          if (info) {
            setDetails({ ...initialData, ...info } as BranchCompanyInfo);
          } else {
            setDetails(initialData);
          }
        })
        .catch((err) => {
          console.error('Firma bilgileri yüklenirken hata:', err);
          setError(t('companyDetails.messages.loadError'));
          setDetails(initialData);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [open, companyId, branchId]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setDetails((prev) => ({ ...prev, [name]: value }));
    if (error) setError(null);
  };

  const handleSave = async () => {
    if (!companyId || !branchId) {
      setError(t('companyDetails.messages.branchNotFound'));
      return;
    }

    setSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      // Tek-kaynak SQL: Firm* alanlari update-firm-details ucuna yazilir (Kural 5).
      await updateBranchFirmInfo(companyId, branchId, {
        firmName: details.firmName || undefined,
        firmTaxNumber: details.firmTaxNumber || undefined,
        firmTaxOffice: details.firmTaxOffice || undefined,
        firmAddress: details.firmAddress || undefined,
        firmPhone: details.firmPhone || undefined,
        firmEmail: details.firmEmail || undefined,
        firmWebsite: details.firmWebsite || undefined,
        firmMersisNo: details.firmMersisNo || undefined,
        firmTradeRegistryNo: details.firmTradeRegistryNo || undefined,
      });
      console.log(`✅ [FIRM-UPDATE] Şube firma bilgileri SQL'e kaydedildi (BranchId=${branchId})`);

      setSuccessMessage(t('companyDetails.messages.saveSuccess'));
      setTimeout(() => onClose(true), 800);
    } catch (err: any) {
      console.error('Firma bilgileri kaydedilirken hata:', err);
      setError(`${t('companyDetails.messages.saveError')}${err.message || ''}`);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setError(null);
    setSuccessMessage(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BusinessIcon color="primary" />
            <Box>
              <Typography variant="h6">{t('companyDetails.title')}</Typography>
              <Typography variant="caption" color="text.secondary">{branchName}</Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <GuideButton guideKey="branches.companyDetails" />
            <IconButton edge="end" color="inherit" onClick={handleClose} aria-label={t('common.close')}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
            <CircularProgress />
          </Box>
        )}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {successMessage && <Alert severity="success" sx={{ mb: 2 }}>{successMessage}</Alert>}
        {!loading && (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                margin="dense"
                name="firmName"
                label={t('companyDetails.fields.firmName')}
                value={details.firmName}
                onChange={handleChange}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                margin="dense"
                name="firmTaxNumber"
                label={t('companyDetails.fields.firmTaxNumber')}
                value={details.firmTaxNumber}
                onChange={handleChange}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                margin="dense"
                name="firmTaxOffice"
                label={t('companyDetails.fields.firmTaxOffice')}
                value={details.firmTaxOffice}
                onChange={handleChange}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                margin="dense"
                name="firmTradeRegistryNo"
                label={t('companyDetails.fields.firmTradeRegistryNo')}
                value={details.firmTradeRegistryNo}
                onChange={handleChange}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                margin="dense"
                name="firmMersisNo"
                label={t('companyDetails.fields.firmMersisNo')}
                value={details.firmMersisNo}
                onChange={handleChange}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                margin="dense"
                name="firmAddress"
                label={t('companyDetails.fields.firmAddress')}
                multiline
                rows={3}
                value={details.firmAddress}
                onChange={handleChange}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                margin="dense"
                name="firmPhone"
                label={t('companyDetails.fields.firmPhone')}
                value={details.firmPhone}
                onChange={handleChange}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                margin="dense"
                name="firmEmail"
                label={t('companyDetails.fields.firmEmail')}
                type="email"
                value={details.firmEmail}
                onChange={handleChange}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                margin="dense"
                name="firmWebsite"
                label={t('companyDetails.fields.firmWebsite')}
                value={details.firmWebsite}
                onChange={handleChange}
                variant="outlined"
                placeholder={t('companyDetails.fields.websitePlaceholder')}
              />
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} color="inherit" disabled={saving}>
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          disabled={saving || loading}
        >
          {saving ? <CircularProgress size={24} color="inherit" /> : t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BranchCompanyDetailsModal;
